import { useState, useMemo } from "react";
import type { DailyPoint } from "@/lib/surf";
import { degToCompass, weatherLabel } from "@/lib/surf";
import { Wind, Waves, CloudSun } from "lucide-react";

type RangeDays = 7 | 16;

export const DailyForecast = ({ daily = [] }: { daily?: DailyPoint[] }) => {
  const [range, setRange] = useState<RangeDays>(7);

  const list = daily ?? [];
  const visible = useMemo(() => list.slice(0, range), [list, range]);
  const truncated = range > list.length;

  if (!list.length) return null;

  const maxWave = Math.max(...visible.map(d => d.waveHeightMax), 0.5);
  const maxWind = Math.max(...visible.map(d => d.windSpeedMax), 5);

  const fmtDay = (iso: string) => {
    const d = new Date(iso + "T00:00");
    return {
      dow: d.toLocaleDateString([], { weekday: "short" }),
      day: d.toLocaleDateString([], { day: "numeric", month: "short" }),
    };
  };

  const RangeBtn = ({ d }: { d: RangeDays }) => (
    <button
      onClick={() => setRange(d)}
      className={`px-3 py-1 text-xs font-bold uppercase tracking-wider transition-all ${
        range === d
          ? "bg-gradient-sunset text-accent-foreground"
          : "border border-border text-muted-foreground hover:text-foreground"
      }`}
    >
      {d}d
    </button>
  );

  return (
    <div className="py-6 px-4 border-t border-border">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-primary font-bold">Long-range outlook</div>
          <h3 className="font-display text-xl sm:text-2xl md:text-3xl text-foreground mt-1">
            {range}-Day Forecast
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <RangeBtn d={7} />
          <RangeBtn d={16} />
        </div>
      </div>

      <div className="flex items-center gap-3 text-xs text-muted-foreground mb-3">
        <span className="flex items-center gap-1"><Waves className="h-3 w-3 text-ocean" /> Swell</span>
        <span className="flex items-center gap-1"><Wind className="h-3 w-3 text-sunset" /> Wind</span>
        <span className="flex items-center gap-1"><CloudSun className="h-3 w-3" /> Weather</span>
      </div>

      {truncated && (
        <p className="mb-3 text-[11px] text-muted-foreground italic">
          Marine model returns up to {daily.length} days — showing all available.
        </p>
      )}

      <div className="overflow-x-auto -mx-2 px-2 pb-2">
        <div className="flex gap-2 min-w-max">
          {visible.map(d => {
            const wavePct = (d.waveHeightMax / maxWave) * 100;
            const windPct = (d.windSpeedMax / maxWind) * 100;
            const { dow, day } = fmtDay(d.date);
            return (
              <div
                key={d.date}
                className="w-16 flex-shrink-0 border-r border-border last:border-r-0 px-2 flex flex-col items-center"
              >
                <div className="text-[11px] font-bold uppercase tracking-wider text-foreground">{dow}</div>
                <div className="text-[9px] text-muted-foreground mb-1">{day}</div>

                <div className="relative h-24 w-full mt-2 flex items-end justify-center gap-1">
                  <div
                    className="w-2.5 bg-gradient-ocean transition-all"
                    style={{ height: `${Math.max(4, wavePct)}%` }}
                  />
                  <div
                    className="w-2.5 bg-gradient-sunset transition-all"
                    style={{ height: `${Math.max(4, windPct)}%` }}
                  />
                </div>

                <div className="mt-2 text-[12px] font-bold text-ocean-deep">
                  {d.waveHeightMax.toFixed(1)}m
                </div>
                <div className="text-[10px] text-muted-foreground">{d.swellPeriodMax.toFixed(0)}s</div>
                <div className="mt-1 text-[10px] font-semibold text-foreground">
                  {d.windSpeedMax.toFixed(0)}
                  <span className="text-muted-foreground">km</span>
                </div>
                <div className="text-[9px] text-muted-foreground" title={`${d.windDirection}°`}>
                  <span className="inline-block" style={{ transform: `rotate(${d.windDirection + 180}deg)` }}>↑</span>{" "}
                  {degToCompass(d.windDirection)}
                </div>
                <div className="mt-1 text-[9px] text-muted-foreground text-center leading-tight">
                  {weatherLabel(d.weatherCode)}
                </div>
                <div className="text-[10px] text-foreground mt-0.5">
                  {Math.round(d.tempMax)}°/{Math.round(d.tempMin)}°
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
